import { motion } from "framer-motion";
import { useIsMobile, usePrefersReducedMotion } from "../../hooks/useIsMobile";

// Minimal global screen overlay — CRT scanlines + vignette + subtle center red
// glow + low-grade static flicker.
//
// On mobile (and for users who prefer reduced motion) we render a static-only
// version: no `mix-blend-mode` animation loops and no large SVG turbulence
// repaints. This keeps the cinematic feel without murdering battery or fps
// on low-end iOS/Android devices. Desktop behaviour is unchanged.

export const ScreenFX = () => {
    const isMobile = useIsMobile();
    const reduceMotion = usePrefersReducedMotion();
    const lite = isMobile || reduceMotion;

    return (
        <div
            aria-hidden
            className="pointer-events-none fixed inset-0 z-[85]"
            data-testid="screen-fx"
        >
            {/* CRT scanlines — static gradient, GPU-cheap */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(to bottom, rgba(255,255,255,0.045) 0px, rgba(255,255,255,0.045) 1px, transparent 1px, transparent 3px)",
                    mixBlendMode: "overlay",
                    opacity: lite ? 0.4 : 0.55
                }}
            />

            {/* Vignette */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)",
                    mixBlendMode: "multiply"
                }}
            />

            {/* Subtle center red glow — animate only on desktop / motion-ok */}
            {lite ? (
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(600px 400px at 50% 45%, rgba(219,22,47,0.05), transparent 70%)",
                        mixBlendMode: "screen",
                        opacity: 0.7
                    }}
                />
            ) : (
                <motion.div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(600px 400px at 50% 45%, rgba(219,22,47,0.06), transparent 70%)",
                        mixBlendMode: "screen"
                    }}
                    animate={{ opacity: [0.55, 0.85, 0.55] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />
            )}

            {/* Low-grade static flicker — desktop only. Heavy turbulence repaint on mobile. */}
            {!lite ? (
                <motion.div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='2.4' numOctaves='1' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/></svg>\")",
                        backgroundSize: "180px 180px",
                        mixBlendMode: "overlay"
                    }}
                    animate={{ opacity: [0.06, 0.11, 0.05, 0.12, 0.06] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
                />
            ) : null}
        </div>
    );
};

export default ScreenFX;
